import React, { useState } from 'react'
import {
  Box,
  Avatar,
  Chip,
  IconButton,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  Note as NoteIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { formatCurrency, formatDate, formatDistanceToNow } from '../../utils/formatters'

const UserDataGrid = ({
  users = [],
  isLoading = false,
  error = null,
  onUserAction,
  onViewDetails,
  pagination = { total: 0, page: 1, limit: 25 },
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedUser(null)
  }

  const handleAction = (action) => {
    if (selectedUser && onUserAction) {
      onUserAction(action, selectedUser)
    }
    handleMenuClose()
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', color: 'success' },
      suspended: { label: 'Askıda', color: 'warning' },
      banned: { label: 'Yasaklı', color: 'error' },
      pending: { label: 'Beklemede', color: 'info' },
    }

    const config = statusConfig[status] || { label: status, color: 'default' }
    return <Chip label={config.label} color={config.color} size="small" />
  }

  const getUserAvatar = (user) => {
    const initials = user.email ? user.email.substring(0, 2).toUpperCase() : '??'
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.875rem',
          bgcolor: user.is_admin ? 'primary.main' : 'secondary.main'
        }}
      >
        {initials}
      </Avatar>
    )
  }

  const columns = [
    {
      field: 'user',
      headerName: 'Kullanıcı',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          {getUserAvatar(params.row)}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {params.row.email || 'Email Yok'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.firebase_uid || (params.row.id ? params.row.id.slice(0, 8) : 'ID Yok')}
              {params.row.is_admin && (
                <Chip
                  label="Admin"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 16, fontSize: '0.625rem' }}
                />
              )}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Durum',
      width: 120,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'credit_balance',
      headerName: 'Kredi Bakiyesi',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoneyIcon color="success" sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'total_rentals',
      headerName: 'Kiralamalar',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'total_spent',
      headerName: 'Toplam Harcama',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? `${params.value} kredi` : '0 kredi'}
        </Typography>
      ),
    },
    {
      field: 'last_login_at',
      headerName: 'Son Giriş',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? formatDistanceToNow(params.value) : 'Hiçbir zaman'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Kayıt Tarihi',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Detayları Görüntüle">
            <IconButton
              size="small"
              onClick={() => onViewDetails && onViewDetails(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="İşlemler">
            <IconButton
              size="small"
              onClick={(event) => handleMenuOpen(event, params.row)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Kullanıcılar yüklenirken hata oluştu: {error.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={users}
        columns={columns}
        loading={isLoading}
        pagination
        page={pagination.page - 1} // DataGrid uses 0-based indexing
        pageSize={pagination.limit}
        rowCount={pagination.total}
        paginationMode="server"
        disableSelectionOnClick
        getRowHeight={() => 70}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        <MenuItem onClick={() => {
          if (onViewDetails) onViewDetails(selectedUser)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Detayları Görüntüle</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleAction('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleAction('credits')}>
          <ListItemIcon>
            <AttachMoneyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Kredi Yönetimi</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleAction('note')}>
          <ListItemIcon>
            <NoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Not Ekle</ListItemText>
        </MenuItem>

        {selectedUser && selectedUser.status === 'active' ? (
          <MenuItem onClick={() => handleAction('suspend')}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Hesabı Askıya Al</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleAction('activate')}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Hesabı Aktifleştir</ListItemText>
          </MenuItem>
        )}

        {selectedUser && selectedUser.status !== 'banned' && (
          <MenuItem onClick={() => handleAction('ban')}>
            <ListItemIcon>
              <PersonIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Kullanıcıyı Yasakla</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

export default UserDataGrid